/**
 * ASCII animation engine — 6 procedural animations rendered to a character grid.
 * Each factory takes (cols, rows) and returns a tick() function that produces a
 * frame string on every call. Designed for ~60fps requestAnimationFrame loops.
 *
 * Animations: fireworks, starfield, lorenz, interference, orbitdecay, accretion
 */

const WORD = 'MOMENT';

type Grid = string[][];

function makeGrid(rows: number, cols: number): Grid {
  return Array.from({ length: rows }, () => Array(cols).fill(' '));
}

function plot(grid: Grid, x: number, y: number, c: string) {
  const px = Math.round(x);
  const py = Math.round(y);
  if (px >= 0 && px < grid[0].length && py >= 0 && py < grid.length) {
    grid[py][px] = c;
  }
}

function renderGrid(grid: Grid): string {
  return grid.map((r) => r.join('')).join('\n');
}

function plotWord(grid: Grid, cx: number, cy: number, word: string) {
  const half = Math.floor(word.length / 2);
  for (let i = 0; i < word.length; i++) {
    plot(grid, cx - half + i, cy, word[i]);
  }
}

export type AnimationTick = () => string;
export type AnimationFactory = (cols: number, rows: number) => AnimationTick;

// ---------------------------------------------------------------------------
// Fireworks
// ---------------------------------------------------------------------------

export const fireworks: AnimationFactory = (cols, rows) => {
  interface Particle {
    x: number; y: number;
    vx: number; vy: number;
    life: number; c: string;
  }

  let particles: Particle[] = [];
  const chars = ['*', '.', "'", '`', ','];
  let nextLetter = 0;

  function spawn() {
    const x = Math.random() * cols;
    const originY = rows * 0.2 + Math.random() * rows * 0.4;
    const count = (15 + Math.random() * 20) | 0;
    const useLetter = Math.random() < 0.4;
    const letter = useLetter ? WORD[nextLetter % WORD.length] : null;
    if (useLetter) nextLetter++;

    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = 0.8 + Math.random() * 2;
      particles.push({
        x, y: originY,
        vx: Math.cos(a) * v, vy: Math.sin(a) * v - 1.5,
        life: (30 + Math.random() * 30) | 0,
        c: letter || chars[(Math.random() * chars.length) | 0],
      });
    }
  }

  let t = 0;
  return () => {
    if (t++ % 60 === 0) spawn();
    const grid = makeGrid(rows, cols);
    for (const p of particles) {
      p.x += p.vx * 0.15;
      p.y += p.vy * 0.15;
      p.vy += 0.04;
      p.life--;
      if (p.life > 0) plot(grid, p.x, p.y, p.c);
    }
    particles = particles.filter((p) => p.life > 0);
    return renderGrid(grid);
  };
};

// ---------------------------------------------------------------------------
// Starfield
// ---------------------------------------------------------------------------

export const starfield: AnimationFactory = (cols, rows) => {
  interface Star {
    x: number; y: number; z: number; c: string | null;
  }

  const stars: Star[] = Array.from({ length: 80 }, () => ({
    x: (Math.random() - 0.5) * 2,
    y: (Math.random() - 0.5) * 2,
    z: Math.random(),
    c: null,
  }));
  const depth = '.+*#@';
  let nextLetter = 0;
  let spawnCounter = 0;

  return () => {
    spawnCounter++;
    const grid = makeGrid(rows, cols);
    for (const s of stars) {
      s.z -= 0.006;
      if (s.z <= 0) {
        s.z = 1;
        s.x = (Math.random() - 0.5) * 2;
        s.y = (Math.random() - 0.5) * 2;
        if (spawnCounter % 8 === 0) {
          s.c = WORD[nextLetter % WORD.length];
          nextLetter++;
        } else {
          s.c = null;
        }
      }
      const sx = ((s.x / s.z) * cols) / 2 + cols / 2;
      const sy = ((s.y / s.z) * rows) / 2 + rows / 2;
      if (s.c) {
        plot(grid, sx, sy, s.c);
      } else {
        const di = Math.min(depth.length - 1, ((1 - s.z) * depth.length) | 0);
        plot(grid, sx, sy, depth[di]);
      }
    }
    return renderGrid(grid);
  };
};

// ---------------------------------------------------------------------------
// Lorenz Attractor
// ---------------------------------------------------------------------------

export const lorenz: AnimationFactory = (cols, rows) => {
  let x = 0.1, y = 0, z = 0;
  const trail: { x: number; y: number }[] = [];
  const maxTrail = 800;
  const density = '.:+*#@';
  const sigma = 10, rho = 28, beta = 8 / 3;
  let t = 0;

  return () => {
    t++;
    const dt = 0.005;
    for (let i = 0; i < 4; i++) {
      const dx = sigma * (y - x) * dt;
      const dy = (x * (rho - z) - y) * dt;
      const dz = (x * y - beta * z) * dt;
      x += dx; y += dy; z += dz;
    }
    const cx = cols / 2, cy = rows / 2;
    const sx = cx + x * (cols / 60);
    const sy = cy + z * (rows / 60) - rows * 0.4;
    trail.push({ x: sx, y: sy });
    if (trail.length > maxTrail) trail.shift();

    const grid = makeGrid(rows, cols);
    trail.forEach((p, i) => {
      const age = i / trail.length;
      const ci = Math.floor(age * (density.length - 1));
      plot(grid, p.x, p.y, density[ci]);
    });

    const fade = Math.sin(t * 0.015) * 0.5 + 0.5;
    if (fade > 0.3) {
      plotWord(grid, Math.round(cx), Math.round(cy - rows * 0.15), WORD);
    }

    return renderGrid(grid);
  };
};

// ---------------------------------------------------------------------------
// Interference
// ---------------------------------------------------------------------------

export const interference: AnimationFactory = (cols, rows) => {
  let t = 0;
  const chars = ' .:-=+*#%@';
  let revealCount = 0;

  return () => {
    t += 0.04;
    const cycleLen = 300;
    const phase = Math.floor(t * 25) % cycleLen;
    if (phase < WORD.length * 25) {
      revealCount = Math.min(WORD.length, Math.floor(phase / 25) + 1);
    } else if (phase > cycleLen - 80) {
      revealCount = Math.max(0, WORD.length - Math.floor((phase - (cycleLen - 80)) / 13));
    }

    const lines: string[] = [];
    const s1x = cols * 0.3, s1y = rows * 0.5;
    const s2x = cols * 0.7, s2y = rows * 0.5;
    const cx = Math.floor(cols / 2), cy = Math.floor(rows / 2);
    const wordStart = cx - Math.floor(WORD.length / 2);

    for (let row = 0; row < rows; row++) {
      let line = '';
      for (let col = 0; col < cols; col++) {
        if (row === cy && col >= wordStart && col < wordStart + revealCount) {
          line += WORD[col - wordStart];
          continue;
        }
        const d1 = Math.sqrt((col - s1x) ** 2 + ((row - s1y) * 2) ** 2);
        const d2 = Math.sqrt((col - s2x) ** 2 + ((row - s2y) * 2) ** 2);
        const v = (Math.sin(d1 * 0.4 - t) + Math.sin(d2 * 0.4 - t)) * 0.25 + 0.5;
        const ci = Math.max(0, Math.min(chars.length - 1, (v * (chars.length - 1)) | 0));
        line += chars[ci];
      }
      lines.push(line);
    }
    return lines.join('\n');
  };
};

// ---------------------------------------------------------------------------
// Orbit Decay
// ---------------------------------------------------------------------------

export const orbitdecay: AnimationFactory = (cols, rows) => {
  const cx = cols / 2, cy = rows / 2;

  interface OrbiterParticle {
    x: number; y: number;
    vx: number; vy: number;
    trail: { x: number; y: number }[];
    maxTrail: number;
  }

  const particles: OrbiterParticle[] = Array.from({ length: 30 }, () => {
    const r = 5 + Math.random() * Math.min(cx, cy) * 0.8;
    const a = Math.random() * Math.PI * 2;
    return {
      x: cx + Math.cos(a) * r,
      y: cy + Math.sin(a) * r * 0.5,
      vx: -Math.sin(a) * Math.sqrt(1 / r) * 8,
      vy: Math.cos(a) * Math.sqrt(1 / r) * 4,
      trail: [],
      maxTrail: (20 + Math.random() * 30) | 0,
    };
  });
  const density = '.:+*#';

  return () => {
    const grid = makeGrid(rows, cols);
    plotWord(grid, Math.round(cx), Math.round(cy), WORD);

    for (let r = 6; r < Math.min(cx, cy) * 0.9; r += 6) {
      for (let a = 0; a < Math.PI * 2; a += 0.3) {
        plot(grid, cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.5, '.');
      }
    }

    for (const p of particles) {
      const dx = cx - p.x, dy = (cy - p.y) * 2;
      const dist = Math.max(3, Math.sqrt(dx * dx + dy * dy));
      const f = 30 / (dist * dist);
      p.vx += (f * dx) / dist;
      p.vy += (f * dy) / dist / 2;
      p.vx *= 0.998;
      p.vy *= 0.998;
      p.x += p.vx;
      p.y += p.vy;
      p.trail.push({ x: p.x, y: p.y });
      if (p.trail.length > p.maxTrail) p.trail.shift();

      if (dist < 5) {
        const r = 10 + Math.random() * Math.min(cx, cy) * 0.7;
        const a = Math.random() * Math.PI * 2;
        p.x = cx + Math.cos(a) * r;
        p.y = cy + Math.sin(a) * r * 0.5;
        p.vx = -Math.sin(a) * Math.sqrt(1 / r) * 8;
        p.vy = Math.cos(a) * Math.sqrt(1 / r) * 4;
        p.trail = [];
      }

      p.trail.forEach((pt, i) => {
        const ci = Math.floor((i / p.trail.length) * (density.length - 1));
        plot(grid, pt.x, pt.y, density[ci]);
      });
      plot(grid, p.x, p.y, '#');
    }

    plotWord(grid, Math.round(cx), Math.round(cy), WORD);
    return renderGrid(grid);
  };
};

// ---------------------------------------------------------------------------
// Accretion
// ---------------------------------------------------------------------------

export const accretion: AnimationFactory = (cols, rows) => {
  const cx = cols / 2, cy = rows / 2;

  interface AccretionParticle {
    x: number; y: number;
    vx: number; vy: number;
    captured: boolean;
  }
  interface DiskParticle {
    r: number; a: number; speed: number;
  }

  const particles: AccretionParticle[] = Array.from({ length: 80 }, () => {
    const a = Math.random() * Math.PI * 2;
    const r = 10 + Math.random() * Math.min(cx, cy) * 0.9;
    return {
      x: cx + Math.cos(a) * r,
      y: cy + Math.sin(a) * r * 0.5,
      vx: -Math.sin(a) * Math.sqrt(50 / r),
      vy: Math.cos(a) * Math.sqrt(50 / r) * 0.5,
      captured: false,
    };
  });
  const density = '.:+*#';
  let diskParticles: DiskParticle[] = [];

  return () => {
    const grid = makeGrid(rows, cols);
    plotWord(grid, Math.round(cx), Math.round(cy), WORD);

    for (const d of diskParticles) {
      d.a += d.speed;
      const px = cx + Math.cos(d.a) * d.r;
      const py = cy + Math.sin(d.a) * d.r * 0.5;
      const ci = Math.max(
        0,
        Math.min(density.length - 1, Math.floor((1 - d.r / 15) * (density.length - 1))),
      );
      plot(grid, px, py, density[ci]);
    }

    for (const p of particles) {
      if (p.captured) continue;
      const dx = cx - p.x, dy = (cy - p.y) * 2;
      const dist = Math.max(2, Math.sqrt(dx * dx + dy * dy));
      const f = 40 / (dist * dist);
      p.vx += (f * dx) / dist;
      p.vy += (f * dy) / dist / 2;
      p.vx *= 0.997;
      p.vy *= 0.997;
      p.x += p.vx;
      p.y += p.vy;

      if (dist < 8) {
        p.captured = true;
        const r = 2 + Math.random() * 5;
        const a = Math.atan2(p.y - cy, p.x - cx);
        diskParticles.push({ r, a, speed: 0.03 + (1 / r) * 0.08 });
        const na = Math.random() * Math.PI * 2;
        const nr = 15 + Math.random() * Math.min(cx, cy) * 0.8;
        p.x = cx + Math.cos(na) * nr;
        p.y = cy + Math.sin(na) * nr * 0.5;
        p.vx = -Math.sin(na) * Math.sqrt(50 / nr);
        p.vy = Math.cos(na) * Math.sqrt(50 / nr) * 0.5;
        p.captured = false;
      }
      plot(grid, p.x, p.y, '*');
    }

    if (diskParticles.length > 200) diskParticles = diskParticles.slice(-200);
    plotWord(grid, Math.round(cx), Math.round(cy), WORD);
    return renderGrid(grid);
  };
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const animations = {
  fireworks,
  starfield,
  lorenz,
  interference,
  orbitdecay,
  accretion,
} as const;

export type AnimationName = keyof typeof animations;
